import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, ShieldIcon, SirenIcon, FlameIcon, FileTextIcon } from 'lucide-react';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// Emergency Services
{
  id: 'es1',
  title: 'Emergency Response Planning',
  description: 'Develop or update emergency response plans for properties',
  category: 'Emergency Services',
  estimatedTime: '5-7 business days',
  fee: '$200.00'
}, {
  id: 'es2',
  title: 'Emergency Access Registration',
  description: 'Register building emergency access information',
  category: 'Emergency Services',
  estimatedTime: '2-3 business days',
  fee: 'No fee'
}, {
  id: 'es3',
  title: 'Medical Emergency Planning',
  description: 'Register medical emergency response protocols',
  category: 'Emergency Services',
  estimatedTime: '3-5 business days',
  fee: '$150.00'
}, {
  id: 'es4',
  title: 'Evacuation Plan Review',
  description: 'Submit and review building evacuation plans',
  category: 'Emergency Services',
  estimatedTime: '5-7 business days',
  fee: '$175.00'
},
// Law Enforcement
{
  id: 'le1',
  title: 'Security System Registration',
  description: 'Register property security systems with local law enforcement',
  category: 'Law Enforcement',
  estimatedTime: '2-3 business days',
  fee: '$50.00'
}, {
  id: 'le2',
  title: 'Neighborhood Watch Program',
  description: 'Register or organize neighborhood watch groups',
  category: 'Law Enforcement',
  estimatedTime: '5-7 business days',
  fee: 'No fee'
}, {
  id: 'le3',
  title: 'Property Crime Prevention',
  description: 'Request security assessment for property crime prevention',
  category: 'Law Enforcement',
  estimatedTime: '7-10 business days',
  fee: 'No fee'
}, {
  id: 'le4',
  title: 'Business Security Consultation',
  description: 'Schedule security consultation for business premises',
  category: 'Law Enforcement',
  estimatedTime: '5-7 business days',
  fee: '$100.00'
},
// Fire Safety
{
  id: 'fs1',
  title: 'Fire Safety Inspection',
  description: 'Schedule comprehensive fire safety inspection',
  category: 'Fire Safety',
  estimatedTime: '3-5 business days',
  fee: '$150.00'
}, {
  id: 'fs2',
  title: 'Fire System Certification',
  description: 'Obtain certification for fire suppression systems',
  category: 'Fire Safety',
  estimatedTime: '5-7 business days',
  fee: '$200.00'
}, {
  id: 'fs3',
  title: 'Fire Drill Planning',
  description: 'Schedule and coordinate fire evacuation drills',
  category: 'Fire Safety',
  estimatedTime: '7-10 business days',
  fee: '$125.00'
}, {
  id: 'fs4',
  title: 'Fire Safety Training',
  description: 'Register for fire safety training programs',
  category: 'Fire Safety',
  estimatedTime: '10-14 business days',
  fee: '$75.00 per person'
},
// Public Safety Permits
{
  id: 'pp1',
  title: 'Special Event Permit',
  description: 'Apply for permits for public events and gatherings',
  category: 'Public Safety Permits',
  estimatedTime: '10-15 business days',
  fee: '$250.00'
}, {
  id: 'pp2',
  title: 'Crowd Management Plan',
  description: 'Submit crowd management plans for large events',
  category: 'Public Safety Permits',
  estimatedTime: '7-10 business days',
  fee: '$200.00'
}, {
  id: 'pp3',
  title: 'Fireworks Display Permit',
  description: 'Apply for professional fireworks display permits',
  category: 'Public Safety Permits',
  estimatedTime: '15-20 business days',
  fee: '$500.00'
}, {
  id: 'pp4',
  title: 'Public Assembly Permit',
  description: 'Obtain permits for temporary public assemblies',
  category: 'Public Safety Permits',
  estimatedTime: '5-7 business days',
  fee: '$150.00'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'Emergency Services':
      return <SirenIcon className="h-6 w-6 text-red-600" data-id="element-4167" />;
    case 'Law Enforcement':
      return <ShieldIcon className="h-6 w-6 text-blue-600" data-id="element-4168" />;
    case 'Fire Safety':
      return <FlameIcon className="h-6 w-6 text-orange-600" data-id="element-4169" />;
    case 'Public Safety Permits':
      return <FileTextIcon className="h-6 w-6 text-purple-600" data-id="element-4170" />;
    default:
      return <ShieldIcon className="h-6 w-6 text-gray-600" data-id="element-4171" />;
  }
};
const ServiceCard = ({
  service
}: {
  service: Service;
}) => {
  return <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-sm transition-all duration-200" data-id="element-4172">
      <div className="p-5 flex flex-col h-full" data-id="element-4173">
        <div className="flex-1" data-id="element-4174">
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-4175">
            {service.title}
          </h3>
          <p className="text-sm text-gray-600 mb-6 line-clamp-2" data-id="element-4176">
            {service.description}
          </p>
        </div>
        <div className="space-y-4" data-id="element-4177">
          <div className="flex items-center space-x-4 text-sm text-gray-500 pt-4 border-t border-gray-100" data-id="element-4178">
            <div className="flex items-center" data-id="element-4179">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-4180">
                ⏱
              </span>
              <span data-id="element-4181">{service.estimatedTime}</span>
            </div>
            <div className="flex items-center" data-id="element-4182">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-4183">
                💰
              </span>
              <span data-id="element-4184">{service.fee}</span>
            </div>
          </div>
          <button className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" data-id="element-4185">
            Apply Now
            <span className="ml-1" data-id="element-4186">→</span>
          </button>
        </div>
      </div>
    </div>;
};
const CategorySection = ({
  category,
  services
}: {
  category: string;
  services: Service[];
}) => {
  return <div className="mb-8" data-id="element-4187">
      <div className="flex items-center mb-4" data-id="element-4188">
        <CategoryIcon category={category} data-id="element-4189" />
        <h2 className="text-xl font-semibold text-gray-900 ml-2" data-id="element-4190">{category}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4191">
        {services.map(service => <ServiceCard key={service.id} service={service} data-id="element-4192" />)}
      </div>
    </div>;
};
export const PublicSafetyPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = services.filter(service => service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredServices.map(service => service.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-4193">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-4194">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-4195">
          <a href="/" className="hover:text-gray-700" data-id="element-4196">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-4197" />
          <span className="text-gray-900" data-id="element-4198">Public Safety Services</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-4199">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-4200">
            Public Safety Services
          </h1>
          <p className="text-gray-600" data-id="element-4201">
            Access emergency services, law enforcement, and safety permits
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-4202">
          <div className="max-w-md relative" data-id="element-4203">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4204">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-4205" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-4206" />
          </div>
        </div>
        {/* Service Categories */}
        {categories.map(category => <CategorySection key={category} category={category} services={filteredServices.filter(service => service.category === category)} data-id="element-4207" />)}
      </div>
    </div>;
};